/**
 * DOM Morphing Engine
 *
 * Compares old/new DOM trees and applies minimal changes,
 * preserving element state (focus, inputs, custom elements).
 * Supports hydration keys for targeted updates.
 */

const HYDRATION_KEY_ATTRIBUTE = 'data-hydration-key';

const MORPH_OPTIONS = {
  childrenOnly: true,
  hydrationMode: false,

  reject(oldNode, newNode) {
    if (newNode.nodeType === Node.TEXT_NODE && newNode.nodeValue?.trim() === '') {
      return true;
    }

    if (
      newNode instanceof HTMLTemplateElement &&
      newNode.shadowRootMode === 'open' &&
      oldNode.parentElement &&
      newNode.parentElement &&
      oldNode.parentElement.tagName === newNode.parentElement.tagName &&
      oldNode.parentElement?.shadowRoot != null
    ) {
      return true;
    }

    if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === 'shopify:rendered_by_section_api') {
      return true;
    }

    return false;
  },
};

/**
 * Morphs one DOM tree into another by comparing nodes and applying minimal changes.
 * @param {Node} oldTree - The existing DOM tree
 * @param {Node | string} newTree - The new DOM tree to morph to
 * @param {Object} [options] - Configuration options
 * @returns {Node} The morphed DOM tree
 */
const morph = (oldTree, newTree, options = MORPH_OPTIONS) => {
  if (!oldTree || !newTree) {
    throw new Error('Both oldTree and newTree must be provided');
  }

  if (typeof newTree === 'string') {
    const template = document.createElement('template');
    template.innerHTML = newTree;
    newTree = template.content.firstChild;
    if (!newTree) {
      throw new Error('newTree string is not valid HTML');
    }
  }

  if (options.hydrationMode && oldTree instanceof Element && newTree instanceof Element) {
    morphHydrationByKey(oldTree, newTree, options);
    return oldTree;
  }

  if (options.childrenOnly) {
    updateChildren(newTree, oldTree, options);
    return oldTree;
  }

  if (newTree.nodeType === 11) {
    throw new Error('newTree should have one root node (not a DocumentFragment)');
  }

  return walk(newTree, oldTree, options);
};

/**
 * Collect targets under a root element that have a non-empty hydration key.
 * @param {Element} root
 * @returns {Element[]}
 */
const collectHydrationTargets = (root) => {
  const targets = [];
  if (root.hasAttribute(HYDRATION_KEY_ATTRIBUTE)) targets.push(root);
  targets.push(...root.querySelectorAll(`[${HYDRATION_KEY_ATTRIBUTE}]`));
  return targets;
};

/**
 * Morph only keyed targets from newRoot into oldRoot.
 * Updates contents of pre-existing targets matched by hydration key value.
 * Does NOT insert new targets or remove missing ones — preserves runtime state.
 * @param {Element} oldRoot
 * @param {Element} newRoot
 * @param {Object} options
 */
const morphHydrationByKey = (oldRoot, newRoot, options) => {
  const oldTargets = collectHydrationTargets(oldRoot);
  const newTargets = collectHydrationTargets(newRoot);

  const oldTargetsByKey = new Map();

  for (const oldTarget of oldTargets) {
    const key = oldTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);
    if (key == null || key === '') continue;

    const existing = oldTargetsByKey.get(key) ?? [];
    existing.push(oldTarget);
    oldTargetsByKey.set(key, existing);
  }

  for (const newTarget of newTargets) {
    const key = newTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);
    if (key == null || key === '') continue;

    const matches = oldTargetsByKey.get(key);
    const oldTarget = matches?.shift();
    if (!oldTarget) continue;

    morph(oldTarget, newTarget, {
      ...options,
      hydrationMode: false,
      childrenOnly: false,
    });
  }
};

/**
 * Walk and morph a DOM tree.
 * @param {Node} newNode
 * @param {Node} oldNode
 * @param {Object} options
 * @returns {Node}
 */
const walk = (newNode, oldNode, options) => {
  if (!oldNode) return newNode;
  if (!newNode) return oldNode;

  if (newNode.isSameNode?.(oldNode)) return oldNode;

  if (newNode.nodeType !== oldNode.nodeType) return newNode;

  if (newNode instanceof Element && oldNode instanceof Element) {
    if (oldNode.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return oldNode;
    if (newNode.tagName !== oldNode.tagName) return newNode;

    const newKey = getNodeKey(newNode, options);
    const oldKey = getNodeKey(oldNode, options);
    if (newKey && oldKey && newKey !== oldKey) return newNode;
  }

  if (
    oldNode instanceof Element &&
    oldNode.hasAttribute('data-skip-node-update') &&
    newNode instanceof Element &&
    newNode.hasAttribute('data-skip-node-update')
  ) {
    updateChildren(newNode, oldNode, options);
  } else {
    updateNode(newNode, oldNode, options);
    updateChildren(newNode, oldNode, options);
  }

  return oldNode;
};

/**
 * Update attributes and special element properties.
 * @param {Node} newNode
 * @param {Node} oldNode
 * @param {Object} options
 */
const updateNode = (newNode, oldNode, options) => {
  options.onBeforeUpdate?.(oldNode, newNode);

  if (
    (newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement) ||
    (newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement)
  ) {
    if (!newNode.hasAttribute('declarative-open')) {
      newNode.open = oldNode.open;
    }
  }

  if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement) {
    for (const attr of ['slot', 'sizes']) {
      const oldValue = oldNode.getAttribute(attr);
      const newValue = newNode.getAttribute(attr);

      if (oldValue !== newValue) {
        oldValue == null ? newNode.removeAttribute(attr) : newNode.setAttribute(attr, oldValue);
      }
    }
  }

  if (newNode instanceof Element && oldNode instanceof Element) {
    if (!oldNode.isEqualNode(newNode)) {
      copyAttributes(newNode, oldNode);
    }
  } else if (newNode instanceof Text || newNode instanceof Comment) {
    if (oldNode.nodeValue !== newNode.nodeValue) {
      oldNode.nodeValue = newNode.nodeValue;
    }
  }

  if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
    updateInput(newNode, oldNode);
  } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
    updateBooleanAttribute(newNode, oldNode, 'selected');
  } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
    updateTextarea(newNode, oldNode);
  }
};

/**
 * Get a node's key using the getNodeKey option or element id.
 * @param {Node | undefined} node
 * @param {Object} [options]
 * @returns {string|number|undefined}
 */
const getNodeKey = (node, options) =>
  options?.getNodeKey?.(node) ?? (node instanceof Element ? node.id : undefined);

/**
 * Update a boolean attribute and its corresponding property.
 * @param {*} newNode
 * @param {*} oldNode
 * @param {string} name
 */
const updateBooleanAttribute = (newNode, oldNode, name) => {
  if (newNode[name] !== oldNode[name]) {
    oldNode[name] = newNode[name];
    if (newNode[name] != null) {
      oldNode.setAttribute(name, '');
    } else {
      oldNode.removeAttribute(name);
    }
  }
};

/**
 * Copy attributes from new node to old node, handling namespaced attributes.
 * Skips resource attributes (src, href, srcset, poster) if value hasn't changed.
 * @param {Element} newNode
 * @param {Element} oldNode
 */
const copyAttributes = (newNode, oldNode) => {
  const oldAttrs = oldNode.attributes;
  const newAttrs = newNode.attributes;

  for (const attr of Array.from(newAttrs)) {
    const { name: attrName, namespaceURI: attrNamespaceURI, value: attrValue } = attr;
    const localName = attr.localName || attrName;

    if (attrName === 'src' || attrName === 'href' || attrName === 'srcset' || attrName === 'poster') {
      if (oldNode.getAttribute(attrName) === attrValue) continue;
    }

    if (attrNamespaceURI) {
      const fromValue = oldNode.getAttributeNS(attrNamespaceURI, localName);
      if (fromValue !== attrValue) {
        oldNode.setAttributeNS(attrNamespaceURI, localName, attrValue);
      }
    } else if (!oldNode.hasAttribute(attrName)) {
      oldNode.setAttribute(attrName, attrValue);
    } else {
      const fromValue = oldNode.getAttribute(attrName);
      if (fromValue !== attrValue) {
        if (attrValue === 'null' || attrValue === 'undefined') {
          oldNode.removeAttribute(attrName);
        } else {
          oldNode.setAttribute(attrName, attrValue);
        }
      }
    }
  }

  for (const attr of Array.from(oldAttrs)) {
    if (attr.specified === false) continue;

    const { name: attrName, namespaceURI: attrNamespaceURI } = attr;
    const localName = attr.localName || attrName;

    if (attrNamespaceURI) {
      if (!newNode.hasAttributeNS(attrNamespaceURI, localName)) {
        oldNode.removeAttributeNS(attrNamespaceURI, localName);
      }
    } else if (!newNode.hasAttribute(attrName)) {
      oldNode.removeAttribute(attrName);
    }
  }
};

/**
 * Update special properties on input elements.
 * @param {HTMLInputElement} newNode
 * @param {HTMLInputElement} oldNode
 */
const updateInput = (newNode, oldNode) => {
  const newValue = newNode.value;

  updateBooleanAttribute(newNode, oldNode, 'checked');
  updateBooleanAttribute(newNode, oldNode, 'disabled');

  if (newNode.indeterminate !== oldNode.indeterminate) {
    oldNode.indeterminate = newNode.indeterminate;
  }

  if (oldNode.type === 'file') return;

  if (newValue !== oldNode.value) {
    oldNode.setAttribute('value', newValue);
    oldNode.value = newValue;
  }

  if (newValue === 'null') {
    oldNode.value = '';
    oldNode.removeAttribute('value');
  }

  if (!newNode.hasAttributeNS(null, 'value')) {
    oldNode.removeAttribute('value');
  } else if (oldNode.type === 'range') {
    oldNode.value = newValue;
  }
};

/**
 * Update the value of a textarea element.
 * @param {HTMLTextAreaElement} newNode
 * @param {HTMLTextAreaElement} oldNode
 */
const updateTextarea = (newNode, oldNode) => {
  const newValue = newNode.value;
  if (newValue !== oldNode.value) {
    oldNode.value = newValue;
  }

  const firstChild = oldNode.firstChild;
  if (firstChild?.nodeType === Node.TEXT_NODE) {
    if (newValue === '' && firstChild.nodeValue === oldNode.placeholder) return;
    firstChild.nodeValue = newValue;
  }
};

/**
 * Recreate app block scripts to force re-execution after morph.
 * @param {Element} container
 */
const recreateAppBlockScripts = (container) => {
  const scripts = container.querySelectorAll('.shopify-app-block script[src]');

  for (const script of scripts) {
    if (!(script instanceof HTMLScriptElement)) continue;

    const parent = script.parentElement;
    if (!parent) continue;

    const newScript = document.createElement('script');
    for (const attr of Array.from(script.attributes)) {
      newScript.setAttribute(attr.name, attr.value);
    }
    if (script.textContent) {
      newScript.textContent = script.textContent;
    }

    script.remove();
    parent.appendChild(newScript);
  }
};

/**
 * Update the children of elements using minimal DOM operations.
 * @param {Node} newNode
 * @param {Node} oldNode
 * @param {Object} options
 */
const updateChildren = (newNode, oldNode, options) => {
  if (
    oldNode instanceof Element &&
    oldNode.hasAttribute('data-skip-subtree-update') &&
    newNode instanceof Element &&
    newNode.hasAttribute('data-skip-subtree-update')
  ) {
    return;
  }

  let oldChild, newChild, morphed, oldMatch;
  let offset = 0;

  for (let i = 0; ; i++) {
    oldChild = oldNode.childNodes[i];
    newChild = newNode.childNodes[i - offset];

    if (!oldChild && !newChild) break;

    if (!newChild) {
      oldChild && oldNode.removeChild(oldChild);
      i--;
      continue;
    }

    if (!oldChild) {
      oldNode.appendChild(newChild);
      offset++;
      continue;
    }

    if (same(newChild, oldChild, options)) {
      morphed = walk(newChild, oldChild, options);
      if (morphed !== oldChild) {
        oldNode.replaceChild(morphed, oldChild);
        offset++;
      }
      continue;
    }

    if (options.reject?.(oldChild, newChild)) {
      newNode.removeChild(newChild);
      i--;
      continue;
    }

    oldMatch = null;
    for (let j = i; j < oldNode.childNodes.length; j++) {
      const potentialOldNode = oldNode.childNodes[j];
      if (potentialOldNode && same(potentialOldNode, newChild, options)) {
        oldMatch = potentialOldNode;
        break;
      }
    }

    if (oldMatch) {
      morphed = walk(newChild, oldMatch, options);
      if (morphed !== oldMatch) offset++;
      oldNode.insertBefore(morphed, oldChild);
    } else if (!getNodeKey(newChild, options) && !getNodeKey(oldChild, options)) {
      morphed = walk(newChild, oldChild, options);
      if (morphed !== oldChild) {
        oldNode.replaceChild(morphed, oldChild);
        offset++;
      }
    } else {
      oldNode.insertBefore(newChild, oldChild);
      offset++;
    }
  }

  if (oldNode instanceof Element) {
    recreateAppBlockScripts(oldNode);
  }
};

/**
 * Check if two nodes are the same (match for morphing).
 * @param {Node} a
 * @param {Node} b
 * @param {Object} options
 * @returns {boolean}
 */
const same = (a, b, options) => {
  if (a.nodeType !== b.nodeType) return false;

  if (a.nodeType === Node.ELEMENT_NODE) {
    if (a instanceof Element && b instanceof Element && a.tagName !== b.tagName) return false;

    const aKey = getNodeKey(a, options);
    const bKey = getNodeKey(b, options);
    if (aKey && bKey && aKey !== bKey) return false;
  }

  if (a.nodeType === Node.TEXT_NODE && b.nodeType === Node.TEXT_NODE) {
    return a.nodeValue?.trim() === b.nodeValue?.trim();
  }

  if (a.nodeType === Node.COMMENT_NODE && b.nodeType === Node.COMMENT_NODE) {
    return a.nodeValue === b.nodeValue;
  }

  return true;
};
